import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import {
  CategoryScale,
  Chart,
  Filler,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js';
import { html, nothing } from 'lit';
import { confirmDialog } from '../confirm-dialog.js';

// Register Chart.js components
Chart.register(LineController, LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Legend, Filler);

type Period = '1w' | '1m' | '3m' | 'all';

interface Org {
  _id: string;
  name: string;
  createdAt: string;
}

interface UserRow {
  _id: string;
  username: string;
  email: string;
  organizationId: string;
  organizationName: string;
  createdAt: string;
}

interface UserInteraction {
  userId: string;
  username: string;
  email: string;
  organizationName: string;
  connectionRequests: number;
  emails: number;
  dms: number;
  total: number;
}

interface OrgInteraction {
  organizationId: string;
  name: string;
  connectionRequests: number;
  emails: number;
  dms: number;
  total: number;
}

interface SuppressedContactRow {
  _id: string;
  email: string;
  date: string;
}

async function adminFetch(url: string, options?: RequestInit): Promise<any> {
  const resp = await fetch(url, options);
  if (resp.status === 401) {
    window.location.href = '/admin/login';
    throw new Error('Unauthorized');
  }
  return resp.json();
}

export const AdminDashboard = stateful(() => {
  const [tab, setTab] = useState<'users' | 'organizations' | 'interactions' | 'suppression'>('interactions');
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [userInteractions, setUserInteractions] = useState<UserInteraction[]>([]);
  const [orgInteractions, setOrgInteractions] = useState<OrgInteraction[]>([]);
  const [period, setPeriod] = useState<Period>('all');
  const [selectedOrgChart, setSelectedOrgChart] = useState('');
  const [loading, setLoading] = useState(true);

  // Create user form state (values read via FormData, hooks kept for ordering)
  useState('');
  useState('');
  useState('');
  useState('');
  const [newOrgName, setNewOrgName] = useState('');
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [suppressedContacts, setSuppressedContacts] = useState<SuppressedContactRow[]>([]);
  const [suppressionSearch, setSuppressionSearch] = useState('');

  let chartInstance: Chart | null = null;

  const loadOrgs = async () => {
    const data = await adminFetch('/admin/api/organizations');
    if (data.ok) setOrgs(data.organizations);
  };

  const loadUsers = async () => {
    const data = await adminFetch('/admin/api/users');
    if (data.ok) setUsers(data.users);
  };

  const loadSuppressedContacts = async () => {
    const data = await adminFetch('/admin/api/suppression');
    if (data.ok) setSuppressedContacts(data.suppressedContacts);
  };

  const loadInteractions = async () => {
    const [userData, orgData] = await Promise.all([
      adminFetch(`/admin/api/interactions/users?period=${period}`),
      adminFetch(`/admin/api/interactions/organizations?period=${period}`),
    ]);
    if (userData.ok) setUserInteractions(userData.users);
    if (orgData.ok) setOrgInteractions(orgData.organizations);
  };

  const loadChart = async (orgId: string) => {
    if (!orgId) return;
    const data = await adminFetch(`/admin/api/interactions/organizations/${orgId}/chart?period=${period}`);
    if (!data.ok) return;

    // Destroy previous chart
    if (chartInstance) {
      chartInstance.destroy();
      chartInstance = null;
    }

    const canvas = document.getElementById('org-chart') as HTMLCanvasElement | null;
    if (!canvas) return;

    chartInstance = new Chart(canvas, {
      type: 'line',
      data: {
        labels: data.labels,
        datasets: [
          {
            label: 'Connection Requests',
            data: data.connectionRequests,
            borderColor: '#003B7E',
            backgroundColor: 'rgba(0, 59, 126, 0.1)',
            fill: true,
            tension: 0.3,
          },
          {
            label: 'E-Mails',
            data: data.emails,
            borderColor: '#16a34a',
            backgroundColor: 'rgba(22, 163, 74, 0.1)',
            fill: true,
            tension: 0.3,
          },
          {
            label: 'LinkedIn DMs',
            data: data.dms,
            borderColor: '#ea580c',
            backgroundColor: 'rgba(234, 88, 12, 0.1)',
            fill: true,
            tension: 0.3,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'top' },
        },
        scales: {
          y: { beginAtZero: true },
        },
      },
    });
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // Check auth first
        await adminFetch('/admin/auth-check');
        await Promise.all([loadOrgs(), loadUsers(), loadInteractions(), loadSuppressedContacts()]);
      } catch {
        // Redirect handled by adminFetch
      }
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    void loadInteractions();
    if (selectedOrgChart) void loadChart(selectedOrgChart);
  }, [period]);

  useEffect(() => {
    if (selectedOrgChart) {
      // Small delay to ensure canvas is in DOM
      setTimeout(() => void loadChart(selectedOrgChart), 50);
    }
  }, [selectedOrgChart]);

  const handleCreateUser = async (form: HTMLFormElement) => {
    setFormError('');
    const fd = new FormData(form);
    const username = ((fd.get('username') as string) ?? '').trim();
    const email = ((fd.get('email') as string) ?? '').trim();
    const password = (fd.get('password') as string) ?? '';
    const organizationId = (fd.get('organizationId') as string) ?? '';
    if (!username || !email || !password) {
      setFormError('Username, E-Mail und Passwort sind erforderlich.');
      return;
    }
    if (password.length < 6) {
      setFormError('Passwort muss mindestens 6 Zeichen lang sein.');
      return;
    }
    setFormLoading(true);
    try {
      const data = await adminFetch('/admin/api/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, email, password, organizationId }),
      });
      if (!data.ok) {
        setFormError(data.error ?? 'Benutzer konnte nicht erstellt werden.');
      } else {
        setShowUserModal(false);
        await loadUsers();
      }
    } catch {
      setFormError('Fehler beim Erstellen.');
    } finally {
      setFormLoading(false);
    }
  };

  const handleCreateOrg = async () => {
    setFormError('');
    if (!newOrgName.trim()) {
      setFormError('Name ist erforderlich.');
      return;
    }
    setFormLoading(true);
    try {
      const data = await adminFetch('/admin/api/organizations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newOrgName.trim() }),
      });
      if (!data.ok) {
        setFormError(data.error ?? 'Organisation konnte nicht erstellt werden.');
      } else {
        setNewOrgName('');
        setShowOrgModal(false);
        await loadOrgs();
      }
    } catch {
      setFormError('Fehler beim Erstellen.');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteOrg = async (id: string) => {
    if (
      !(await confirmDialog('Organisation wirklich löschen?', {
        title: 'Organisation löschen',
        confirmLabel: 'Löschen',
      }))
    )
      return;
    await adminFetch(`/admin/api/organizations/${id}`, { method: 'DELETE' });
    await loadOrgs();
  };

  const handleDeleteUser = async (id: string) => {
    if (!(await confirmDialog('Benutzer wirklich löschen?', { title: 'Benutzer löschen', confirmLabel: 'Löschen' })))
      return;
    await adminFetch(`/admin/api/users/${id}`, { method: 'DELETE' });
    await loadUsers();
  };

  const handleRemoveSuppressedContact = async (id: string, email: string) => {
    if (
      !(await confirmDialog(
        `Remove ${email} from the suppression list? They will become eligible for outreach again.`,
        { title: 'Remove from Suppression List', confirmLabel: 'Remove' },
      ))
    )
      return;
    await adminFetch(`/admin/api/suppression/${id}`, { method: 'DELETE' });
    await loadSuppressedContacts();
  };

  const handleChangeUserOrg = async (userId: string, organizationId: string) => {
    await adminFetch(`/admin/api/users/${userId}/organization`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ organizationId }),
    });
    await loadUsers();
  };

  const handleLogout = async () => {
    await fetch('/admin/logout', { method: 'POST' });
    window.location.href = '/admin/login';
  };

  if (loading) {
    return html`<div class="flex h-screen items-center justify-center">
      <span class="loading loading-spinner loading-lg"></span>
    </div>`;
  }

  const periodTabs = html`
    <div class="join mb-4">
      ${(['1w', '1m', '3m', 'all'] as Period[]).map(
        p => html`
          <button
            class="join-item btn btn-sm ${period === p ? 'btn-primary text-white shadow-md' : 'btn-ghost'}"
            @click=${() => setPeriod(p)}
          >
            ${p === '1w' ? '1 Woche' : p === '1m' ? '1 Monat' : p === '3m' ? '3 Monate' : 'Alle'}
          </button>
        `,
      )}
    </div>
  `;

  const userModal = showUserModal
    ? html`
        <div class="modal modal-open">
          <div class="modal-box">
            <h3 class="mb-4 text-lg font-bold">Neuen Benutzer erstellen</h3>
            ${formError ? html`<div class="alert alert-error mb-3 text-sm">${formError}</div>` : nothing}
            <form
              @submit=${(e: Event) => {
                e.preventDefault();
                handleCreateUser(e.target as HTMLFormElement);
              }}
            >
              <div class="form-control mb-3">
                <label class="label"><span class="label-text">Username</span></label>
                <input name="username" type="text" class="input input-bordered w-full" placeholder="Username" />
              </div>
              <div class="form-control mb-3">
                <label class="label"><span class="label-text">E-Mail</span></label>
                <input name="email" type="email" class="input input-bordered w-full" placeholder="user@example.com" />
              </div>
              <div class="form-control mb-3">
                <label class="label"><span class="label-text">Passwort</span></label>
                <input name="password" type="text" class="input input-bordered w-full" placeholder="Min. 6 Zeichen" />
              </div>
              <div class="form-control mb-3">
                <label class="label"><span class="label-text">Organisation</span></label>
                <select name="organizationId" class="select select-bordered w-full">
                  <option value="">— Keine —</option>
                  ${orgs.map(o => html`<option value=${o._id}>${o.name}</option>`)}
                </select>
              </div>
              <div class="modal-action">
                <button
                  type="button"
                  class="btn"
                  @click=${() => {
                    setShowUserModal(false);
                    setFormError('');
                  }}
                >
                  Abbrechen
                </button>
                <button type="submit" class="btn btn-primary ${formLoading ? 'loading' : ''}" ?disabled=${formLoading}>
                  Erstellen
                </button>
              </div>
            </form>
          </div>
          <div class="modal-backdrop" @click=${() => setShowUserModal(false)}></div>
        </div>
      `
    : nothing;

  const orgModal = showOrgModal
    ? html`
        <div class="modal modal-open">
          <div class="modal-box">
            <h3 class="mb-4 text-lg font-bold">Neue Organisation erstellen</h3>
            ${formError ? html`<div class="alert alert-error mb-3 text-sm">${formError}</div>` : nothing}
            <div class="form-control mb-3">
              <label class="label"><span class="label-text">Name</span></label>
              <input
                type="text"
                class="input input-bordered w-full"
                placeholder="Firmenname"
                .value=${newOrgName}
                @input=${(e: InputEvent) => setNewOrgName((e.target as HTMLInputElement).value)}
              />
            </div>
            <div class="modal-action">
              <button
                class="btn"
                @click=${() => {
                  setShowOrgModal(false);
                  setFormError('');
                }}
              >
                Abbrechen
              </button>
              <button
                class="btn btn-primary ${formLoading ? 'loading' : ''}"
                @click=${handleCreateOrg}
                ?disabled=${formLoading}
              >
                Erstellen
              </button>
            </div>
          </div>
          <div class="modal-backdrop" @click=${() => setShowOrgModal(false)}></div>
        </div>
      `
    : nothing;

  const usersTab = html`
    <div class="mb-4 flex items-center justify-between">
      <h2 class="text-xl font-bold">Benutzer</h2>
      <button
        class="btn btn-primary btn-sm gap-1"
        @click=${() => {
          setFormError('');
          setShowUserModal(true);
        }}
      >
        <span class="icon-[material-symbols--person-add] text-lg"></span>
        Benutzer hinzufügen
      </button>
    </div>
    <div class="overflow-x-auto">
      <table class="table-zebra table w-full">
        <thead>
          <tr>
            <th>E-Mail</th>
            <th>Username</th>
            <th>Organisation</th>
            <th>Erstellt</th>
            <th>Aktionen</th>
          </tr>
        </thead>
        <tbody>
          ${users.map(
            u => html`
              <tr>
                <td>${u.email}</td>
                <td>${u.username}</td>
                <td>
                  <select
                    class="select select-bordered select-sm"
                    .value=${u.organizationId}
                    @change=${(e: Event) => handleChangeUserOrg(u._id, (e.target as HTMLSelectElement).value)}
                  >
                    <option value="">— Keine —</option>
                    ${orgs.map(
                      o => html`<option value=${o._id} ?selected=${o._id === u.organizationId}>${o.name}</option>`,
                    )}
                  </select>
                </td>
                <td>${u.createdAt ? new Date(u.createdAt).toLocaleDateString('de-DE') : '—'}</td>
                <td>
                  <button class="btn btn-ghost btn-xs text-error" @click=${() => handleDeleteUser(u._id)}>
                    <span class="icon-[material-symbols--delete] text-base"></span>
                  </button>
                </td>
              </tr>
            `,
          )}
        </tbody>
      </table>
    </div>
  `;

  const organizationsTab = html`
    <div class="mb-4 flex items-center justify-between">
      <h2 class="text-xl font-bold">Organisationen</h2>
      <button
        class="btn btn-primary btn-sm gap-1"
        @click=${() => {
          setFormError('');
          setShowOrgModal(true);
        }}
      >
        <span class="icon-[material-symbols--add-circle] text-lg"></span>
        Organisation erstellen
      </button>
    </div>
    <div class="overflow-x-auto">
      <table class="table-zebra table w-full">
        <thead>
          <tr>
            <th>Name</th>
            <th>Erstellt</th>
            <th>Aktionen</th>
          </tr>
        </thead>
        <tbody>
          ${orgs.map(
            o => html`
              <tr>
                <td class="font-medium">${o.name}</td>
                <td>${o.createdAt ? new Date(o.createdAt).toLocaleDateString('de-DE') : '—'}</td>
                <td>
                  <button class="btn btn-ghost btn-xs text-error" @click=${() => handleDeleteOrg(o._id)}>
                    <span class="icon-[material-symbols--delete] text-base"></span>
                  </button>
                </td>
              </tr>
            `,
          )}
        </tbody>
      </table>
    </div>
  `;

  const filteredSuppressedContacts = suppressionSearch.trim()
    ? suppressedContacts.filter(s => s.email.toLowerCase().includes(suppressionSearch.trim().toLowerCase()))
    : suppressedContacts;

  const suppressionTab = html`
    <div class="mb-4 flex items-center justify-between">
      <h2 class="text-xl font-bold">Suppression List</h2>
    </div>
    <div class="mb-4">
      <input
        type="text"
        class="input input-bordered w-full max-w-md"
        placeholder="Search by email..."
        .value=${suppressionSearch}
        @input=${(e: InputEvent) => setSuppressionSearch((e.target as HTMLInputElement).value)}
      />
    </div>
    <div class="overflow-x-auto">
      <table class="table-zebra table w-full">
        <thead>
          <tr>
            <th>Email</th>
            <th>Date Added</th>
            <th>Aktionen</th>
          </tr>
        </thead>
        <tbody>
          ${filteredSuppressedContacts.length === 0
            ? html`<tr>
                <td colspan="3" class="py-8 text-center opacity-60">No suppressed contacts found.</td>
              </tr>`
            : filteredSuppressedContacts.map(
                s => html`
                  <tr>
                    <td class="font-medium">${s.email}</td>
                    <td>${s.date ? new Date(s.date).toLocaleDateString('de-DE') : '—'}</td>
                    <td>
                      <button
                        class="btn btn-ghost btn-xs text-error"
                        @click=${() => handleRemoveSuppressedContact(s._id, s.email)}
                      >
                        <span class="icon-[material-symbols--delete] text-base"></span>
                      </button>
                    </td>
                  </tr>
                `,
              )}
        </tbody>
      </table>
    </div>
  `;

  const interactionsTab = html`
    ${periodTabs}

    <h3 class="mb-3 text-lg font-bold">Benutzer Interaktionen</h3>
    <div class="mb-8 overflow-x-auto">
      <table class="table-zebra table w-full">
        <thead>
          <tr>
            <th>Benutzer</th>
            <th>Organisation</th>
            <th>Connection Requests</th>
            <th>E-Mails</th>
            <th>LinkedIn DMs</th>
            <th>Gesamt</th>
          </tr>
        </thead>
        <tbody>
          ${userInteractions.map(
            u => html`
              <tr>
                <td>
                  <div class="font-medium">${u.email || u.username}</div>
                </td>
                <td><span class="badge badge-ghost badge-sm">${u.organizationName || '—'}</span></td>
                <td>${u.connectionRequests}</td>
                <td>${u.emails}</td>
                <td>${u.dms}</td>
                <td class="font-semibold">${u.total}</td>
              </tr>
            `,
          )}
        </tbody>
      </table>
    </div>

    <h3 class="mb-3 text-lg font-bold">Organisation Interaktionen</h3>
    <div class="mb-8 overflow-x-auto">
      <table class="table-zebra table w-full">
        <thead>
          <tr>
            <th>Organisation</th>
            <th>Connection Requests</th>
            <th>E-Mails</th>
            <th>LinkedIn DMs</th>
            <th>Gesamt</th>
            <th>Graph</th>
          </tr>
        </thead>
        <tbody>
          ${orgInteractions.map(
            o => html`
              <tr>
                <td class="font-medium">${o.name}</td>
                <td>${o.connectionRequests}</td>
                <td>${o.emails}</td>
                <td>${o.dms}</td>
                <td class="font-semibold">${o.total}</td>
                <td>
                  <button class="btn btn-ghost btn-xs" @click=${() => setSelectedOrgChart(o.organizationId)}>
                    <span class="icon-[material-symbols--bar-chart] text-base"></span>
                  </button>
                </td>
              </tr>
            `,
          )}
        </tbody>
      </table>
    </div>

    ${selectedOrgChart
      ? html`
          <div class="card bg-base-100 p-6 shadow-md">
            <div class="mb-4 flex items-center justify-between">
              <h3 class="text-lg font-bold">
                Interaktionen: ${orgInteractions.find(o => o.organizationId === selectedOrgChart)?.name ?? ''}
              </h3>
              <button class="btn btn-ghost btn-sm" @click=${() => setSelectedOrgChart('')}>
                <span class="icon-[material-symbols--close] text-lg"></span>
              </button>
            </div>
            <div style="height: 350px; position: relative;">
              <canvas id="org-chart"></canvas>
            </div>
          </div>
        `
      : nothing}
  `;

  return html`
    <div class="bg-base-200 flex h-screen flex-col">
      <nav class="navbar ndi-nav shadow-md">
        <div class="flex-1">
          <a class="flex cursor-pointer items-center gap-2 px-4">
            <span class="ndi-logo-text text-2xl">NDI</span>
            <span class="text-sm font-medium opacity-80">Admin Dashboard</span>
          </a>
        </div>
        <div class="flex gap-1">
          ${(
            [
              ['interactions', 'Interaktionen', 'bar-chart'],
              ['users', 'Benutzer', 'group'],
              ['organizations', 'Organisationen', 'business'],
              ['suppression', 'Suppression List', 'block'],
            ] as const
          ).map(
            ([key, label, icon]) => html`
              <button
                class="btn btn-ghost btn-sm ${tab === key ? 'btn-active' : ''} gap-1.5"
                @click=${() => setTab(key as any)}
              >
                <span class="icon-[material-symbols--${icon}] text-lg"></span>
                ${label}
              </button>
            `,
          )}
        </div>
        <div class="flex-none px-2">
          <button class="btn btn-ghost btn-sm gap-1" @click=${handleLogout}>
            <span class="icon-[material-symbols--logout] text-lg"></span>
            Logout
          </button>
        </div>
      </nav>
      <main class="flex-1 overflow-auto p-6">
        ${tab === 'users'
          ? usersTab
          : tab === 'organizations'
            ? organizationsTab
            : tab === 'suppression'
              ? suppressionTab
              : interactionsTab}
      </main>
    </div>
    ${userModal} ${orgModal}
  `;
});
